import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

const typographyVariants = cva("", {
  variants: {
    variant: {
      display:
        "font-heading text-5xl md:text-7xl font-bold tracking-tight",
      h1: "font-heading text-4xl md:text-5xl font-bold tracking-tight",
      h2: "font-heading text-3xl md:text-4xl font-bold tracking-tight",
      h3: "font-heading text-2xl md:text-3xl font-semibold tracking-tight",
      h4: "font-heading text-xl md:text-2xl font-semibold tracking-tight",
      lead: "text-xl text-muted-foreground",
      body: "text-base",
      small: "text-sm",
      muted: "text-sm text-muted-foreground",
    },
  },
  defaultVariants: {
    variant: "body",
  },
})

export interface TypographyProps
  extends React.HTMLAttributes<HTMLElement>,
    VariantProps<typeof typographyVariants> {
  asChild?: boolean
}

const Typography = React.forwardRef<HTMLElement, TypographyProps>(
  ({ className, variant, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "p"
    return (
      <Comp
        className={cn(typographyVariants({ variant, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Typography.displayName = "Typography"

// Display
interface DisplayProps extends React.HTMLAttributes<HTMLHeadingElement> {
  asChild?: boolean
}

const Display = React.forwardRef<HTMLHeadingElement, DisplayProps>(
  ({ className, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "h1"
    return (
      <Comp
        className={cn(typographyVariants({ variant: "display", className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Display.displayName = "Display"

// H1
interface H1Props extends React.HTMLAttributes<HTMLHeadingElement> {
  asChild?: boolean
}

const H1 = React.forwardRef<HTMLHeadingElement, H1Props>(
  ({ className, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "h1"
    return (
      <Comp
        className={cn(typographyVariants({ variant: "h1", className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
H1.displayName = "H1"

// H2
interface H2Props extends React.HTMLAttributes<HTMLHeadingElement> {
  asChild?: boolean
}

const H2 = React.forwardRef<HTMLHeadingElement, H2Props>(
  ({ className, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "h2"
    return (
      <Comp
        className={cn(typographyVariants({ variant: "h2", className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
H2.displayName = "H2"

// H3
interface H3Props extends React.HTMLAttributes<HTMLHeadingElement> {
  asChild?: boolean
}

const H3 = React.forwardRef<HTMLHeadingElement, H3Props>(
  ({ className, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "h3"
    return (
      <Comp
        className={cn(typographyVariants({ variant: "h3", className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
H3.displayName = "H3"

// H4
interface H4Props extends React.HTMLAttributes<HTMLHeadingElement> {
  asChild?: boolean
}

const H4 = React.forwardRef<HTMLHeadingElement, H4Props>(
  ({ className, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "h4"
    return (
      <Comp
        className={cn(typographyVariants({ variant: "h4", className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
H4.displayName = "H4"

// Lead
const Lead = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => {
    return (
      <p
        className={cn(typographyVariants({ variant: "lead", className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Lead.displayName = "Lead"

// Body (Text alias)
const Text = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => {
    return (
      <p
        className={cn(typographyVariants({ variant: "body", className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Text.displayName = "Text"

// Small
const Small = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => {
    return (
      <p
        className={cn(typographyVariants({ variant: "small", className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Small.displayName = "Small"

// Muted
const Muted = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => {
    return (
      <p
        className={cn(typographyVariants({ variant: "muted", className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Muted.displayName = "Muted"

export {
  Typography,
  typographyVariants,
  Display,
  H1,
  H2,
  H3,
  H4,
  Lead,
  Text,
  Small,
  Muted,
}
